import {
	FormsPayloadEntities,
	MessageBox,
	Mobile,
	ProjectHelper,
	HTTPRequestResponse
} from '@bimser/common';
import {
	BCExtremeGrid,
	BCExtremeGridEntities,
	BCLoading,
	BCNotification,
	findMessage,
	NumberFormatter
} from '@bimser/components';
import {
	FilterOperations,
	IExtremeGridColumn
} from '@bimser/components/dist/BCExtremeGrid/entities';
import {
	compareMatchWithCollection,
	createGuid,
	debounce,
	FormItemTypes,
	IDictionary,
	IReadOnlyDictionary,
	isNullOrUndefined,
	MLHelper,
	mobile
} from '@bimser/core';
import dayjs from 'dayjs';
import { Map } from 'immutable';
import { JSX } from 'react';
import { EContextMenuTargetTypes, IControlProperties } from '../../../../entities';
import {
	DataGridDisplayType,
	DataSourceType,
	GridLinesType,
	IDataSource,
	IOnLoadArgs,
	IPropertyField,
	SaveModeType
} from '../../../../entities/common';
import {
	IBaseExecuteActionArgs,
	ParameterItemType
} from '../../../../entities/common/executeActionArgs';
import { IUserEvent } from '../../../../entities/services';
import { getDataSourceData, geti18nValue } from '../../../../helper';
import { getActionValue } from '../../../../sagas/ruleValidation/action';
import { evaluateIt } from '../../../../sagas/ruleValidation/rule';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import Control from '../../base/Control';
import {
	EBatchFieldTypes,
	IBatchChangesEventArgs,
	IBatchOperation,
	ICellPreparedEventArgs,
	IControlExecutedServerEvent,
	IDataLoadEventArgs,
	IEditingStartEventArgs,
	IPropertyChangedEventArgs,
	IRowInsertedEventArgs,
	IRowInsertingEventArgs,
	IRowRemovedEventArgs,
	IRowRemovingEventArgs,
	IRowUpdatedEventArgs,
	IRowUpdatingEventArgs,
	ISelectionChangingEventArgs,
	ISetCellValueChangeEventArgs
} from '../../entities/EventArgs';
import { filterFields, getValueControl } from '../../helper';
import './assets/DataGridComponentStyles.scss';
import {
	DataTypes,
	IControlDataGridColumn,
	IDataGridAlternateRowColorOptions,
	IDataGridColumnCollection,
	IDataGridColumnOptions,
	IDataGridComboBoxColumn,
	IDataGridDetailRowOptions,
	IDataGridEditingOptions,
	IDataGridFilteringOptions,
	IDataGridGroupingOptions,
	IDataGridHiddenRowValueOptions,
	IDataGridPagingOptions,
	IDataGridProperties,
	IDataGridRow,
	IDataGridRowCollection,
	IDataGridRowDraggingOptions,
	IDataGridSearchingOptions,
	IDataGridSelectionOptions,
	IDataGridSortingOptions,
	IExecuteActionButton
} from './entities';
import { DataGridEditType, IChangeSetArgs } from './entities/operations';
import { IDataGridColumnSortArgs } from './entities/operations/columnSort';
import {
	compareDeletingRowByCellType,
	convertFieldsForMobile,
	getComponentData,
	getDataGridProps,
	getExtremeGridRows,
	onRowInserted,
	onRowInserting,
	setDefaultDataGridProps,
	subscribeMobilEvents
} from './helpers';
import { getColumnProperties } from './helpers/column/getColumnProperties';
import { getDynamicSelectColumnProperties } from './helpers/column/getSelectColumnProperties';
import { getNewRow } from './helpers/events/onCommitChanges';
import { getBaseRowData, triggerRowEvents } from './helpers/events/rowEvents';
import { getOperationData } from './helpers/getOperation';

import localizedFormat from 'dayjs/plugin/localizedFormat';
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';

dayjs.extend(localizedFormat);

export default class DataGrid extends CompositeDataBoundControl {
	// DataSource
	columns: IDataGridColumnCollection;
	rows: IDataGridRowCollection;
	dataSource: IDataSource;
	dataSourceType: DataSourceType;
	height: number;
	displayType: DataGridDisplayType;
	selectionForm?: boolean;
	// Options
	columnSettings: IDataGridColumnOptions;
	detailRow: IDataGridDetailRowOptions;
	editing: IDataGridEditingOptions;
	filtering: IDataGridFilteringOptions;
	grouping: IDataGridGroupingOptions;
	paging: IDataGridPagingOptions;
	searching: IDataGridSearchingOptions;
	selection: IDataGridSelectionOptions;
	sorting: IDataGridSortingOptions;
	toolbarActionButtons: IExecuteActionButton<
		IBaseExecuteActionArgs<Exclude<ParameterItemType, 'selectedRow'>>
	>[];
	alternateColor: IDataGridAlternateRowColorOptions;
	virtualScrolling: boolean;
	exportToExcel: boolean;
	showClearAllButton: boolean;
	gridLines?: GridLinesType;
	gridBorder: boolean;
	hiddenRowColumnName: string;
	hoverStateEnabled: boolean;
	hiddenRowValueMatchers: IDataGridHiddenRowValueOptions;
	batchOperations?: Array<IBatchOperation>;
	selectedRowKeys?: Array<number | string>;
	selectedRowsData?: Array<IDictionary<any>>;
	rowDragging?: IDataGridRowDraggingOptions;
	columnHidingEnabled?: boolean;
	columnFixingEnabled?: boolean;
	wordWrapEnabled?: boolean;
	saveMode?: SaveModeType;
	cacheSettingsEnabled?: boolean;
	pendingEdit: boolean = false;

	__columns: Array<BCExtremeGridEntities.IExtremeGridColumn>;
	__gridId: string;
	__filtering: Array<BCExtremeGridEntities.IFilterItem> = [];
	__sorting: Array<BCExtremeGridEntities.ISortingColumn> = [];
	__formulaControls: string[] = [];
	__editingColumnsValues: any[] = [];
	__dataSourceLoadedAtLeastOnce?: boolean;
	__columnTriggerDataSourceList: IDictionary<string> = {};
	__columnDataSourceList: IDictionary<string> = {};
	__columnEditorOptionsList: IDictionary<string> = {};
	__createColumnList: IDictionary<string> = {};
	__dataGridRef: any = null;
	__totalCount: number;
	__searhcValue: string;
	__summaryValue: IDictionary<any> = {};
	__methods?: any;
	__documentId: number = 0;
	__formSaveControl: boolean = false;
	__formWidth: number;
	__hasPendingInsertRows: boolean = false;
	__commitIdFixed: boolean = false;
	__isUpdateColumnCells: boolean = false;
	/** Kullanmak için ilgili kolon isimlerini ekleyiniz
	 */
	__editableWithCellMode: Array<string> = [];
	__noRenderContentCellValidation: (row?: any, columnName?: string) => boolean;
	constructor() {
		super();
		this._hideCommonLoadingOverlay = true;

		this.sortColumn = this.sortColumn.bind(this);
		this.getDatasourceUpdate = this.getDatasourceUpdate.bind(this);
		this.addValueByAutoIncrement = this.addValueByAutoIncrement.bind(this);
		this.addValueByUniqData = this.addValueByUniqData.bind(this);
		this.getRowData = this.getRowData.bind(this);
		this.onContextMenuOpen = this.onContextMenuOpen.bind(this);
		this.onRowChangesChange = debounce(this.onRowChangesChange.bind(this), 300) as any;
		this.onRowDoubleClick = this.onRowDoubleClick.bind(this);
		this.onColumnResizeChange = this.onColumnResizeChange.bind(this);
		this.onCommitChanges = this.onCommitChanges.bind(this);
		this.onEditorCreating = this.onEditorCreating.bind(this);
		this.onEditorPreparing = this.onEditorPreparing.bind(this);
		this.onFilteringChange = debounce(this.onFilteringChange.bind(this), 300) as any;
		this.onGridUpdated = this.onGridUpdated.bind(this);
		this.onPageIndexChange = this.onPageIndexChange.bind(this);
		this.onPageSizeChange = this.onPageSizeChange.bind(this);
		this.onRowInserted = this.onRowInserted.bind(this);
		this.onRowInserting = this.onRowInserting.bind(this);
		this.onRowRemoved = this.onRowRemoved.bind(this);
		this.onRowRemoving = this.onRowRemoving.bind(this);
		this.onRowUpdated = this.onRowUpdated.bind(this);
		this.onRowUpdating = this.onRowUpdating.bind(this);
		this.setCellValueEvent = this.setCellValueEvent.bind(this);
		this.onSearchingChange = this.onSearchingChange.bind(this);
		this.onSelectionChange = this.onSelectionChange.bind(this);
		this.onSelectionChanging = this.onSelectionChanging.bind(this);
		this.onSortingChange = this.onSortingChange.bind(this);
		this.onForceRefresh = this.onForceRefresh.bind(this);
		this.noDataTemplateAndSetMaxSize = this.noDataTemplateAndSetMaxSize.bind(this);
		this.__onLoadData = this.__onLoadData.bind(this);
		this.__onExportData = this.__onExportData.bind(this);
		this.__getSearchValueFilters = this.__getSearchValueFilters.bind(this);
		this.subscribeOnPropertyChanged = this.subscribeOnPropertyChanged.bind(this);
		this.onSummaryChanged = this.onSummaryChanged.bind(this);
		this.onEditingStart = this.onEditingStart.bind(this);
		this.__addFilter = this.__addFilter.bind(this);
		this.getHeaderFilterData = this.getHeaderFilterData.bind(this);
		this.getGridRef = this.getGridRef.bind(this);
		this.onFilterClear = this.onFilterClear.bind(this);
		this.onColumnVisibilityChange = this.onColumnVisibilityChange.bind(this);
		this.getMoreRows = this.getMoreRows.bind(this);
		this.onInitNewRow = this.onInitNewRow.bind(this);
		this.onReorder = this.onReorder.bind(this);
		this.onClearAll = this.onClearAll.bind(this);
		this.__getChangesWithCompleteSchema = this.__getChangesWithCompleteSchema.bind(this);
		this.__getSkipValueForPagination = this.__getSkipValueForPagination.bind(this);
		this.onExportingAsBase64 = this.onExportingAsBase64.bind(this);
		this.onColumnFixingChange = this.onColumnFixingChange.bind(this);
		this.onCommitChangesSelf = this.onCommitChangesSelf.bind(this);
		this.getSelfUrl = this.getSelfUrl.bind(this);
		this.onLoadSelf = this.onLoadSelf.bind(this);
		this.getFilterColumnName = this.getFilterColumnName.bind(this);
		this.__onLoadExportDataSelfMode = this.__onLoadExportDataSelfMode.bind(this);
		this.onCellPrepared = this.onCellPrepared.bind(this);
		this.checkDuplicateColumn = this.checkDuplicateColumn.bind(this);
		this.checkDuplicateColumnControl = this.checkDuplicateColumnControl.bind(this);
		this.onEditCanceling = this.onEditCanceling.bind(this);
		setDefaultDataGridProps.call(this);
		subscribeMobilEvents.call(this);
	}

	subscribeOnPropertyChanged(column: IDataGridComboBoxColumn, index: any) {
		const control = column.editControl;
		const propertyPaths: string[] = [];

		if (control.dataSource?.parameters?.length) {
			control.dataSource.parameters.forEach(param => {
				if (param?.valueModifier == 'fieldSelection') {
					const fieldArr = param.field.split('.');
					const controlId = fieldArr.shift();
					const propertyPath = fieldArr.join('.');
					propertyPaths.push(propertyPath);

					if (!this.__columnTriggerDataSourceList[`${controlId}${column.name}`]) {
						this.__columnTriggerDataSourceList[`${controlId}${column.name}`] =
							'Subscribed';

						/**
						 *
						 * @param fieldParams
						 * After the related column fields changes
						 * triggered by prepareSelectColumnParameters
						 * and fetching new data by new params.
						 */
						const setColumnEditorOptions = (fieldParams = {}) => {
							this.__proxyChanging.loading = true;
							getDynamicSelectColumnProperties
								.call(this, column, index, fieldParams)
								.then(result => {
									this.__columnEditorOptionsList[`${this.name}${column.name}`] =
										result;
									this.__columns = this.__columns.map(col => {
										if (col.name === column.name) {
											col.editorOptions['options'] = result.items;
											col.data = result.data;
										}

										return col;
									});
									this.__proxyChanging.loading = false;
									this.emit('trigger:OnDataGridEditingRowChange', null);
								});
						};

						/**
						 *
						 * @param rowData rows currentData
						 * @param columnName current Column name
						 *
						 * After the column changes sending this row to this area and
						 * getting value by column type and trigger related datasource
						 * column.
						 *
						 */
						const prepareSelectColumnParameters = (
							rowData: IReadOnlyDictionary<any>,
							columnName?: string
						) => {
							const fieldParams: IDictionary<any> = {};

							Object.keys(rowData).forEach(key => {
								const column = this.columns.find(c => c.name == key);
								switch (column?.editType) {
									case 'comboBox': {
										if (column.dbType?.type === 'object') {
											const cellPropertyPaths = propertyPaths.filter(path =>
												path.includes(`columns.${key}.data`)
											);

											cellPropertyPaths.forEach(cellPropertyPath => {
												if (cellPropertyPath) {
													const cellValue = getCellValue(
														rowData[key],
														this.columns.find(c => c.name == key)
															?.editType
													);
													const cellColumnData = this.__columns.find(
														col => col.name === key
													)?.data;
													const cellColumnValueExp = (
														this.columns.find(col => col.name === key)
															?.editControl as any
													)?.dataSource?.valueExpr;
													const parameterValue = cellColumnData?.find(
														data =>
															data[cellColumnValueExp] === cellValue
													);
													const matcherColumnName =
														cellPropertyPath.split('.')[
															cellPropertyPath.split('.').length - 1
														];
													fieldParams[
														`${this.name}.${cellPropertyPath}`
													] = parameterValue?.[matcherColumnName] || null;
												}
											});
										} else {
											const cellPropertyPaths = propertyPaths.filter(path =>
												path.includes(`columns.${key}.value`)
											);

											cellPropertyPaths.forEach(cellPropertyPath => {
												if (cellPropertyPath) {
													const cellValue = getCellValue(
														rowData[key],
														this.columns.find(c => c.name == key)
															?.editType
													);
													fieldParams[
														`${this.name}.${cellPropertyPath}`
													] = cellValue || null;
												}
											});
										}

										break;
									}
									case 'textBox':
									case 'timePicker':
									case 'datePicker':
									case 'dateTimePicker':
									case 'numberBox':
									case 'checkBox':
									case 'dateRangePicker': {
										const cellPropertyPaths = propertyPaths.filter(path =>
											path.includes(`columns.${key}.value`)
										);

										cellPropertyPaths.forEach(cellPropertyPath => {
											if (cellPropertyPath) {
												const cellValue = getCellValue(
													rowData[key],
													this.columns.find(c => c.name == key)?.editType
												);
												fieldParams[`${this.name}.${cellPropertyPath}`] =
													cellValue || null;
											}
										});
										break;
									}
									case 'multiLanguageTextBox': {
										const cellPropertyPaths = propertyPaths.filter(path =>
											path.includes(`columns.${key}.culture`)
										);

										cellPropertyPaths.forEach(cellPropertyPath => {
											if (cellPropertyPath) {
												const path = cellPropertyPath.split('.');
												const culture = path[path.length - 1];
												const cellValue = _get(
													rowData,
													`${key}.${culture}`
												);
												fieldParams[`${this.name}.${cellPropertyPath}`] =
													cellValue || null;
											}
										});
										break;
									}
									default:
										break;
								}
							});

							if (
								isNullOrUndefined(columnName) ||
								propertyPath.includes(`columns.${columnName}`)
							) {
								setColumnEditorOptions(fieldParams);
							}
						};

						/**
						 *
						 * @param value column current value
						 * @param type  column type
						 * Preparing value for some different kind of column types
						 * such as multilanguage or select.
						 * Could make changes when needed specific modifications
						 * by column paths or data reserve model.
						 */
						const getCellValue = (value: any, type: DataGridEditType) => {
							switch (type) {
								case 'comboBox': {
									return value?.value || value?.text || value;
								}
								case 'multiLanguageTextBox': {
									if (!isNullOrUndefined(value)) {
										return (
											value[MLHelper.currentLanguage.culture] ||
											value[MLHelper.defaultLanguage.culture]
										);
									}
									break;
								}
								default:
									return value;
							}
						};

						// #region SUBSCRIBE TO ON PARAM CHANGE : START
						(this.__form[controlId] as Control)?.subscribe(
							'event:OnPropertyChanged',
							(args: IPropertyChangedEventArgs<any>) => {
								if (propertyPath === args.name) {
									setColumnEditorOptions();
								}
							}
						);

						// #endregion SUBSCRIBE TO ON PARAM CHANGE : END

						(this.__form[controlId] as Control)?.subscribe(
							'event:OnDataLoad',
							(args: IDataLoadEventArgs) => {
								setColumnEditorOptions();
							}
						);

						// #region [Preparing DataSource-Select column parameters for loading]
						(this.__form[controlId] as Control)?.subscribe(
							'event:SetCellValue',
							(args: ISetCellValueChangeEventArgs) => {
								const rowData = _merge(args.currentRowData, args.newData);

								prepareSelectColumnParameters(rowData, args.columnName);
							}
						);

						(this.__form[controlId] as Control)?.subscribe(
							'event:OnEditingStart',
							(args: IEditingStartEventArgs) => {
								prepareSelectColumnParameters(args.row);
							}
						);
						// #endregion
					}
				}
			});
		} else {
			this.__columnDataSourceList[`${this.name}${column.name}`] = 'Subscribed';
		}
	}

	onExportingAsBase64(base64: string, type: boolean) {
		Mobile.MobileView.exportData(
			this.caption.text[this.__form.currentLanguage.culture],
			base64,
			type
		);
	}

	isMobile() {
		return Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable;
	}

	initialize() {
		super.initialize();
		this.__gridId = `runtime_${this.__form.getPanelId()}_${createGuid()}`;
		this.__generateFormulaControls();

		this.__form.subscribe('event:OnAfterSave', () => {
			this.__formSaveControl = true;

			if (
				this.saveMode == 'self' &&
				this.__form.documentId !== this.__documentId &&
				this.rows.filter(r => isNullOrUndefined(r.rowId) || r.rowId == 0).length > 0
			) {
				this.onInsertSelf(
					this.rows.filter(r => isNullOrUndefined(r.rowId) || r.rowId == 0)
				);
			} else {
				this.__formSaveControl = false;
			}

			this.__documentId = this.__form.documentId;
		});
	}

	async getSelfUrl(operationName) {
		const deploymentUrl = ProjectHelper.getDeploymentUrl(this.__form.projectName, null);
		return `${deploymentUrl}/${this.__form.name}/GridOperations/${operationName}`;
	}

	getSelfData(url): Promise<HTTPRequestResponse<any>> {
		return new Promise<HTTPRequestResponse<any>>((resolve, reject) => {
			getOperationData(url, {
				documentId: this.__documentId,
				controlName: this.name,
				columns: this.columns,
				loadOptions: this.getLoadOptions()
			})
				.then(response => {
					resolve(response);
				})
				.catch(err => {
					MessageBox.error({ error: err.exception.data });
					reject();
				});
		});
	}

	async onLoadSelf() {
		const url = await this.getSelfUrl('getRows');
		try {
			const response = await this.getSelfData(url);
			if (Array.isArray(response?.data?.rows)) {
				const tId = this.__form.beginTransaction();
				const rows = response?.data?.rows;

				this.__proxyChanging.rows = rows;

				this.__totalCount = response.data.count;
				await this.__form.endTransaction(tId);

				this.emit('event:OnDataLoad', { data: response.data.result } as IDataLoadEventArgs);

				return { rows: rows, totalCount: response.data.count };
			}
		} catch (e) {
			this.__proxyChanging.rows = [];
			this.__totalCount = null;
		} finally {
			this.__formSaveControl = false;
			this.__proxyChanging.loading = false;
		}
	}

	async __onLoad({ externalData, reload, forceRefresh, loadOptions }: IOnLoadArgs) {
		if (this.saveMode == 'self' && this.__documentId) {
			this.onLoadSelf();
		} else {
			if (
				this.__form.displayMode === 'preview' ||
				(!this.__isInitialized && this.dataSource?.parameters?.length && !reload)
			) {
				return;
			}

			if (this.dataSource?.name && !this.dataSource.runAtServer) {
				this.__proxyChanging.loading = true;

				try {
					const response = await getDataSourceData({
						dataSource: this.dataSource,
						projectName: this.__form.projectName,
						clientCacheId: this.__form.__windowId,
						fieldParams: externalData?.fieldParams,
						reload,
						forceRefresh,
						loadOptions: loadOptions || this.getLoadOptions(),
						projectVersion: this.__form.packageVersion
					});

					if (Array.isArray(response?.data?.result)) {
						const tId = this.__form.beginTransaction();
						const rows = response.data.result.map(this.getRowData);

						this.__proxyChanging.rows = rows;

						this.__totalCount = response.data.count;
						await this.__form.endTransaction(tId);
						this.__dataSourceLoadedAtLeastOnce = true;

						this.emit('event:OnDataLoad', {
							data: response.data.result
						} as IDataLoadEventArgs);

						return { rows: rows, totalCount: response.data.count };
					}
				} catch (e) {
					this.__proxyChanging.rows = [];
					this.__totalCount = null;
				} finally {
					this.__proxyChanging.loading = false;
				}
			} else {
				this.__proxyChanging.loading = false;
			}
		}
	}

	private getRowData(item: any, index: any) {
		const currentColumnNames = this.columns?.map(({ name }) => name) || [];

		item = currentColumnNames.reduce(
			(result, name) =>
				!Object.keys(item).includes(name) ? { ...result, [name]: null } : result,
			item
		);

		const cells = currentColumnNames.map((columnName, columnIndex) => {
			const foundColumn = this.columns.find(i => i.name === columnName) || null;
			let columnText = null;

			const columnData = _get(item, columnName) ?? _get(item, columnName.split('.'));

			if (!isNullOrUndefined(columnData)) {
				columnText =
					foundColumn?.editType === 'multiLanguageTextBox'
						? geti18nValue(columnData, this.__form.currentLanguage)
						: columnData;
				columnText = typeof columnText === 'object' ? columnText.toString() : columnText;
			}
			return {
				editType: foundColumn?.editType,
				name: columnName,
				text: columnText,
				value: !isNullOrUndefined(columnData) ? columnData : null,
				index: columnIndex
			};
		});

		return {
			cells,
			index,
			selected: false,
			data: item
		};
	}

	async __loadProperties(data: IDataGridProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.columns = data.columns;
		if (
			data.columns?.length &&
			!this.__columns?.length &&
			!this.__createColumnList[`${this.name}`]
		) {
			this.getColumns();
			this.__createColumnList[`${this.name}`] = 'subscribed';
		} else if (this.__columns.length) {
			this.getColumnsUpdate();
		}
		const prevRows = this.rows;

		this.__proxyChanged.rows = data.rows;
		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.dataSourceType = data.dataSourceType;
		this.__proxyChanged.columnSettings = data.columnSettings;
		this.__proxyChanged.detailRow = data.detailRow;
		this.__proxyChanged.editing = data.editing;
		this.__proxyChanged.filtering = data.filtering;
		this.__proxyChanged.grouping = data.grouping;
		this.__proxyChanged.paging = data.paging;
		this.__proxyChanged.searching = data.searching;
		this.__proxyChanged.selection = data.selection;
		this.__proxyChanged.sorting = data.sorting;
		this.__proxyChanged.toolbarActionButtons = data.toolbarActionButtons;
		this.__proxyChanged.height = data.height;
		this.__proxyChanged.displayType = data.displayType;
		this.__proxyChanged.alternateColor = data.alternateColor;
		this.__proxyChanged.virtualScrolling = data.virtualScrolling;
		this.__proxyChanged.exportToExcel = data.exportToExcel;
		this.__proxyChanged.showClearAllButton = data.showClearAllButton;
		this.__proxyChanged.gridLines = data.gridLines;
		this.__proxyChanged.gridBorder = data.gridBorder;
		this.__proxyChanged.hiddenRowColumnName = data.hiddenRowColumnName;
		this.__proxyChanged.hiddenRowValueMatchers = data.hiddenRowValueMatchers;
		this.__proxyChanged.batchOperations = data.batchOperations;
		this.__proxyChanged.hoverStateEnabled = data.hoverStateEnabled;
		this.__proxyChanged.selectedRowKeys = data.selectedRowKeys;
		this.__proxyChanged.selectedRowsData = data.selectedRowsData;
		this.__proxyChanged.rowDragging = data.rowDragging;
		this.__proxyChanged.wordWrapEnabled = data.wordWrapEnabled;
		this.__proxyChanged.columnHidingEnabled = data.columnHidingEnabled;
		this.__proxyChanged.columnFixingEnabled = data.columnFixingEnabled;
		this.__proxyChanged.saveMode = data.saveMode;
		this.__proxyChanged.cacheSettingsEnabled = data.cacheSettingsEnabled;

		if (data.selectionForm) {
			this.__proxyChanged.selectionForm = false;
			this.__getSelectionFormUpdate(this.getDatasourceUpdate(prevRows));
		}

		if (this.saveMode == 'self') {
			if (this.__form.documentId != this.__documentId) {
				this.__documentId = this.__form.documentId;
				this.reload(true);
			}

			if (
				this.__documentId > 0 &&
				!this.__formSaveControl &&
				this.rows.filter(r => isNullOrUndefined(r.rowId) || r.rowId == 0).length > 0
			) {
				this.onInsertSelf(
					this.rows.filter(r => isNullOrUndefined(r.rowId) || r.rowId == 0)
				);
			}
		}

		if (this.__form?.windowSize?.width) {
			if (this.__formWidth && this.__form.windowSize?.width != this.__formWidth) {
				this.__dataGridRef?.instance?.()?.updateDimensions?.();
			}
			this.__formWidth = this.__form.windowSize.width;
		}
	}

	async getColumnsUpdate() {
		try {
			this.__columns.forEach(column => {
				const col = this.columns.find(c => c.name === column.name);
				if (col.visible !== column.visible) {
					column.visible = col.visible;
					column.allowNull = !col.visible || col.autoIncrement || col.allowNull;
				}
				if (col.editingEnabled !== column.allowEditing) {
					const allowEditing =
						col.dbType?.type == 'none' && col.editType == 'actionButton'
							? false
							: col.editingEnabled && !col.autoIncrement;
					column.allowEditing = allowEditing;
					column.editorOptions.disabled = !allowEditing;
				}
				if (col.disableToExport !== column.disabledToExport) {
					column.disabledToExport = col.disableToExport;
				}
			});
		} catch (e) {
			console.log(e);
		}
	}

	getColumns() {
		try {
			setTimeout(async () => {
				this.__columns = await Promise.all(
					this.columns.map((column, index) =>
						getColumnProperties.call(this, column, index)
					)
				);
				this.__columns.forEach(column => {
					if (this.__columnEditorOptionsList[`${this.name}${column.name}`]) {
						column.editorOptions['options'] =
							this.__columnEditorOptionsList[`${this.name}${column.name}`]?.['items'];
						column.data =
							this.__columnEditorOptionsList[`${this.name}${column.name}`]?.['data'];
					}
				});
				if (
					(Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable) &&
					this.__form.__mobileFormView == 3 &&
					this.editing?.mode == 'cell'
				) {
					this.__proxyChanging.editing = { ...this.editing, mode: 'row' };
				}
				Mobile.MobileView.setControlProperties(
					this.name,
					this.__getComponentData(),
					this.__form?.__windowId || ''
				);

				this.__proxyChanging.detailRow = { ...this.detailRow };

				setTimeout(() => {
					this.__form.__forceUpdate?.('__columns');
				}, 10);
			}, 100);
		} catch (e) {
			console.log(e);
		}
	}

	__getChangesWithCompleteSchema(changes: any) {
		const valueColumnNames = Object.keys(changes);
		this.columns.forEach(column => {
			if (!valueColumnNames.includes(column.name)) {
				if (column.dbType.type === 'boolean') {
					changes[column.name] = false;
				} else {
					changes[column.name] = null;
				}
			}
		});
		return changes;
	}

	async __getSelectionFormUpdate(prevData: BCExtremeGridEntities.IDataSource) {
		const tId = this.__form.beginTransaction();
		let isCancel = false;
		const uniqueDataField = this.getUniqueDataField();
		const dataSource = prevData?.data ?? [];
		let newRows = this.rows;
		for (const row of this.rows) {
			const rowFinds = dataSource.filter(
				r => r[uniqueDataField] == row.cells.find(c => c.name === uniqueDataField)?.value
			);
			if (rowFinds.length === 0) {
				const onRowInsertingArgs: any = { cancel: false, row: row, rows: this.rows };
				await onRowInserting.call(this, onRowInsertingArgs);
				if (!onRowInsertingArgs.cancel) {
					onRowInserted.call(this, { row: onRowInsertingArgs.row });
				} else {
					newRows = newRows.filter(
						r =>
							r.cells.find(c => c.name === uniqueDataField)?.value !=
							row.cells.find(c => c.name === uniqueDataField)?.value
					);
					isCancel = true;
				}
			}
		}

		if (isCancel) {
			this.__proxyChanging.rows = newRows;
		}

		await this.__form.endTransaction(tId);
		if (isCancel) {
			this.onFilterClear();
		}
	}

	addValueByUniqData(uniqDataValues: any, changes: any, uniqDataField: string) {
		const uniqDataFieldValue = uniqDataValues.rowValue || null;
		const currentValue = this.rows.some(
			row => row.cells.find(cell => cell.name === uniqDataField).value === uniqDataFieldValue
		);

		if (!currentValue) {
			const updatedColumns = this.__editingColumnsValues.map(column => {
				const findColumn = this.__columns.find(item => item.name == column.name);
				if (findColumn?.editorType == 'SelectBox' && typeof column.value == 'string') {
					return {
						...column,
						rowValue: findColumn.editorOptions.options.find(
							option => option.key == column.value
						)
					};
				}
				return column;
			});
			changes = updatedColumns.reduce(
				(result, current) => ({ ...result, [current.name]: current.rowValue }),
				changes
			);
			changes = this.checkColumnFormulas(changes);

			const rows = this.rows.map(row =>
				row.cells.reduce(
					(result, current) => ({ ...result, [current.name]: current.value }),
					{}
				)
			);
			const changesWithEmptyFields = this.__getChangesWithCompleteSchema(cloneDeep(changes));

			const onRowInsertingArgs: any = {
				cancel: false,
				row: changesWithEmptyFields,
				rows: rows
			};
			onRowInserting.call(this, onRowInsertingArgs).then(() => {
				if (!onRowInsertingArgs.cancel) {
					onRowInserted.call(this, { row: onRowInsertingArgs.row });
					changes = getBaseRowData.call(this, onRowInsertingArgs.row);
					const payload: BCExtremeGridEntities.IChangeSet = {
						data: changes,
						key: changes[this.getUniqueDataField()],
						type: 'insert'
					};
					this.onCommitChanges(payload, changes);
				}
			});
		} else {
			Mobile.MobileView.gridRowCommitResponse({ status: false });
			this.__form.showToaster(
				findMessage.get('100652'),
				findMessage
					.get('101633')
					.replace('{0}', uniqDataFieldValue)
					.replace('{1}', uniqDataField),
				'Warning'
			);
		}
	}

	addValueByAutoIncrement(changes: any) {
		const rows = this.rows.map(row =>
			row.cells.reduce(
				(result, current) => ({ ...result, [current.name]: current.value }),
				{}
			)
		);

		const updatedColumns = this.__editingColumnsValues.map(column => {
			const findColumn = this.__columns.find(item => item.name == column.name);
			if (findColumn?.editorType == 'SelectBox' && typeof column.value == 'string') {
				return {
					...column,
					rowValue: findColumn.editorOptions.options.find(
						option => option.key == column.value
					)
				};
			}
			return column;
		});

		changes = updatedColumns.reduce(
			(result, current) => ({ ...result, [current.name]: current.rowValue }),
			changes
		);

		changes = this.columns.reduce((result, column) => {
			if (column.autoIncrement) {
				const sortedValue = [...rows]
					.sort((a, b) => (Number(b[column.name]) || 0) - (Number(a[column.name]) || 0))
					.find(x => x);

				return { ...result, [column.name]: Number(sortedValue?.[column.name]) + 1 || 1 };
			}
			return result;
		}, changes);

		changes = this.checkColumnFormulas(changes);
		const changesWithEmptyFields = this.__getChangesWithCompleteSchema(cloneDeep(changes));

		const onRowInsertingArgs: any = { cancel: false, row: changesWithEmptyFields, rows: rows };
		onRowInserting.call(this, onRowInsertingArgs).then(() => {
			if (!onRowInsertingArgs.cancel) {
				onRowInserted.call(this, { row: onRowInsertingArgs.row });
				changes = getBaseRowData.call(this, onRowInsertingArgs.row);
				const payload: BCExtremeGridEntities.IChangeSet = {
					data: changes,
					key: changes[this.getUniqueDataField()],
					type: 'insert'
				};
				this.onCommitChanges(payload, changes);
			}
		});
	}

	getUniqueDataField = () => this.columns?.find(item => item.isPrimaryKey)?.name || 'id';

	reload(force?: boolean) {
		if (!isNullOrUndefined(this.dataSource?.name)) {
			if (this.dataSource?.parameters?.length || force) {
				this.__dataSourceValueEntryFlag = false;
				this.__reloadDataSource(this.dataSource, force, false, true).catch(() => {
					this.__proxyChanging.loading = false;
				});
			} else {
				this.__onLoad({ reload: false });
			}
		}
	}

	getCellValue = (rowIndex: number, cellIndex: number) =>
		_get(this.rows, [rowIndex, 'cells', cellIndex, 'value']);

	getRowValues = (rowIndex: number) => this.rows?.[rowIndex]?.data;

	getSelectedRows = () => this.rows.filter(i => i.selected);

	getDatasourceHiddens(rows: IDataGridRowCollection) {
		const column = this.columns.find(c => c.name === this.hiddenRowColumnName);
		const dataType = column ? DataTypes[column?.dbType?.type as any] : 'string';
		const editType = column?.editType || 'textBox';
		return (
			rows?.reduce((result, row, index) => {
				const isHidden = this.hiddenRowValueMatchers?.values?.some(compareValue =>
					compareMatchWithCollection({
						value: _get(row.data, this.hiddenRowColumnName),
						dataType,
						compareValue,
						editType
					})
				);
				return isHidden ? result : [...result, getExtremeGridRows.call(this, row, index)];
			}, []) || []
		);
	}

	getDatasourceUpdate(rows: IDataGridRowCollection): BCExtremeGridEntities.IDataSource {
		const data = this.getDatasourceHiddens(rows);
		return {
			data,
			totalCount: this.__totalCount || data?.length || 0
		};
	}

	newRow = (oldRow?: IDataGridRow): IDataGridRow => ({
		cells:
			this.columns.map((column, columnIndex) => ({
				editType: column.editType,
				entityPath: (column.editControl as any)?.EntityPath,
				name: column.name,
				index: columnIndex,
				text:
					!isNullOrUndefined(oldRow?.data?.[column.name]) &&
					oldRow?.data?.[column.name] != ''
						? oldRow?.data?.[column.name]?.toString() || ''
						: column.autoIncrement
						? this.rows.length + 1
						: column.defaultValue || '', //NOSONAR
				value:
					!isNullOrUndefined(oldRow?.data?.[column.name]) &&
					oldRow?.data?.[column.name] != ''
						? oldRow?.data?.[column.name]
						: column.autoIncrement
						? this.rows.length + 1
						: column.defaultValue //NOSONAR
			})) || [],
		index: this.rows.length,
		selected: false,
		rowId: oldRow?.rowId || 0
	});

	updateColumnCells = async (columnName, value) => {
		const tId = this.__form.beginTransaction();
		this.__isUpdateColumnCells = true;
		setTimeout(() => {
			const currentRows = [];
			this.rows.forEach(row => {
				row.cells.forEach(cell => {
					if (cell.name == columnName) {
						cell.value = value;
					}
				});
				row.data[columnName] = value;
				currentRows.push(row);
			});
			this.__proxyChanging.rows = currentRows;
		}, 10);
		await this.__form.endTransaction(tId);
	};

	deleteRows = async ids => {
		if (ids?.length) {
			const tId = this.__form.beginTransaction();
			this.__proxyChanging.rows = this.rows.filter(
				row => ids.indexOf(row.data[this.getUniqueDataField()]) == -1
			);
			await this.__form.endTransaction(tId);
		}
	};

	addColumns = (columns: IDataGridColumnCollection) => {
		const currentColumnNames = this.columns.map(c => c.name);
		const duplicate = columns.find(c => currentColumnNames.includes(c.name));

		if (duplicate) {
			console.error('DataGrid.addColumns : duplicate column error');
		} else {
			this.__proxyChanging.columns = [...this.columns, ...columns];
			this.getColumns();
		}
	};

	insertRow = async (data: IDictionary<any>) => {
		this.rowChangeAction({
			type: 'insert',
			key: data[this.getUniqueDataField()],
			data
		});

		await this.__form.applyChanges();
	};

	insertRows = async (data: IDictionary<any>[]) => {
		for (const item of data) {
			await this.insertRow(item);
		}
	};

	updateRow = async (data: IDictionary<any>) => {
		this.rowChangeAction({
			type: 'update',
			key: data[this.getUniqueDataField()],
			data
		});

		await this.__form.applyChanges();
	};

	updateRows = async (data: IDictionary<any>[]) => {
		for (const item of data) {
			await this.updateRow(item);
		}
	};

	removeRow = async (key: any) => {
		this.rowChangeAction({
			type: 'remove',
			key
		});

		await this.__form.applyChanges();
	};

	removeRows = async (keys: any[]) => {
		for (const key of keys) {
			await this.removeRow(key);
		}
	};

	rowChangeAction = (args: IChangeSetArgs) => {
		let nullOrConflict: boolean = false;
		switch (args?.type) {
			case 'insert':
				nullOrConflict = this.getNullOrConflictControl(args.data, args.key);
				break;
			case 'remove':
				nullOrConflict = !args.key;
				break;
			case 'update':
				const uniqueDataField = this.getUniqueDataField();
				if (args.data) {
					if (args.data[uniqueDataField] === undefined) {
						args.data = { ...args.data, [uniqueDataField]: args.key };
					}

					if (args.data[uniqueDataField] !== args.key) {
						nullOrConflict = this.getNullOrConflictControl(args.data, args.key);
					}
				} else {
					nullOrConflict = true;
				}

				break;
		}

		if (!nullOrConflict) {
			this.onCommitChanges(args as any, args.data);
		}
	};

	getNullOrConflictControl(row: any, key: any): boolean {
		let control = false;
		const uniqueDataField = this.getUniqueDataField();
		if ((isNullOrUndefined(key) || key == '') && key != 0) {
			BCNotification.warning({
				message: findMessage.get('101632').replace('{0}', uniqueDataField)
			});
			control = true;
		} else {
			const item = this.rows.find(
				(_item: any) =>
					_item.cells.find(r => r.name == uniqueDataField)?.value == row[uniqueDataField]
			);
			if (!isNullOrUndefined(item)) {
				BCNotification.warning({
					message: findMessage
						.get('101633')
						.replace('{0}', row[uniqueDataField])
						.replace('{1}', uniqueDataField)
				});
				control = true;
			}
		}

		return control;
	}

	// getSummaryItems(): Array<BCExtremeGridEntities.ISummaryItem> {
	//     let properties = DataGridAPI.getProperties(this.__gridId);
	//     return properties?.summary?.totalItems || [];
	// }

	formulaEvaluate(formula: string, row: any): any {
		if (isNullOrUndefined(formula)) {
			return formula;
		}

		const controlRegex = /\[([\s\S]*?)\]/gm;
		const match = formula?.match(controlRegex);

		match?.forEach(source => {
			const rawPath = source.replace('[', '').replace(']', '').split('.');
			const sourceId = rawPath[0];
			const sourcePropPath = rawPath.slice(1, rawPath.length);

			let v: any;

			const sourceItem = (this.__form as any)[sourceId];

			if (sourceItem.controlType === FormItemTypes.EControlTypes.DataGrid) {
				if (
					sourcePropPath.indexOf('columns') === 0 &&
					sourcePropPath.indexOf('value') === 2
				) {
					sourcePropPath.pop();
					const columnName = sourcePropPath.pop();
					v = row[columnName];
				} else if (sourcePropPath.indexOf('columns') === 0) {
					// This condition area is left for the purpose of preventing errors from previously developed formulas.
					const columnName = sourcePropPath.pop();
					v = row[columnName];
				} else {
					v = _get(sourceItem, sourcePropPath);
				}
			} else {
				v = _get(sourceItem, sourcePropPath);
			}

			if (isNullOrUndefined(v)) {
				v = '';
			}
			if (typeof v === 'string') {
				v = `'${v}'`;
			}
			if (typeof v === 'undefined') {
				v = '0';
			}

			formula = formula.replace(source, v);
		});
		return evaluateIt(formula);
	}

	__generateFormulaControls() {
		this.columns?.forEach(c => {
			if (!c.isPrimaryKey && c?.dbType?.type === 'decimal' && c.formula) {
				const match = c.formula.match(/\[([\s\S]*?)\]/gm);

				this.__formulaControls =
					match?.map(source => {
						const rawPath = source.replace('[', '').replace(']', '').split('.');

						return [rawPath[0], ...rawPath.slice(1, rawPath.length)].join('.');
					}) || [];
			}
		});
	}

	__controlsUpdated(controls: FormsPayloadEntities.IPropertyChangeEventArgs[]) {
		controls.forEach(c => {
			const dataField = [c.id, ...c.dataField].join('.');

			if (this.__formulaControls?.includes(dataField)) {
				this.__proxyChanging.rows = this.rows.map(r => {
					r.data = this.checkColumnFormulas(r.data);

					Object.keys(r.data).forEach(key => {
						const index = r.cells.findIndex(cell => cell.name === key);
						r.cells[index].value = r.data[key];
					});
					return r;
				});
			}
		});
	}

	checkColumnFormulas(row: any): any {
		return this.columns.reduce((result, column) => {
			if (!column.isPrimaryKey && column?.dbType?.type === 'decimal' && column.formula) {
				if (column.precision) {
					const f = new NumberFormatter();
					f.init(column.precision, false);
					const evaluated = this.formulaEvaluate(column.formula, row);
					const formatted = f.format(evaluated, null) || '';
					const replaced =
						this.__form.currentLanguage.culture !== 'en-US'
							? formatted.replace('.', '').replace(',', '.')
							: formatted;
					const parsed = parseFloat(replaced);

					return { ...result, [column.name]: parsed };
				}

				return { ...result, [column.name]: this.formulaEvaluate(column.formula, row) };
			}
			return result;
		}, row);
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields();
		const captionItems: IPropertyField[] = MLHelper.supportedLanguages.map(lang => ({
			name: lang.culture,
			type: 'string'
		}));
		const columnPropertiesModel: IPropertyField[] = [
			{
				name: 'align',
				type: 'string'
			},
			{
				name: 'autoIncrement',
				type: 'boolean'
			},
			{
				name: 'caption',
				type: 'object',
				items: captionItems
			},
			{
				name: 'dbType',
				type: 'object',
				items: [
					{
						name: 'columnName',
						type: 'string'
					},
					{
						name: 'type',
						type: 'string'
					}
				]
			},
			{
				name: 'formatType',
				type: 'object',
				items: [
					{
						name: 'format',
						type: 'string'
					},
					{
						name: 'type',
						type: 'string'
					}
				]
			},
			{
				name: 'editType',
				type: 'string'
			},
			{
				name: 'editingEnabled',
				type: 'boolean'
			},
			{
				name: 'filtering',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'operation',
						type: 'string'
					}
				]
			},
			{
				name: 'format',
				type: 'string'
			},
			{
				name: 'isPrimaryKey',
				type: 'boolean'
			},
			{
				name: 'name',
				type: 'string'
			},
			{
				name: 'precision',
				type: 'number'
			},
			{
				name: 'sorting',
				type: 'object',
				items: [
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'index',
						type: 'number'
					},
					{
						name: 'order',
						type: 'string'
					}
				]
			},
			{
				name: 'useThousandSeparator',
				type: 'boolean'
			},
			{
				name: 'visible',
				type: 'boolean'
			},
			{
				name: 'width',
				type: 'number'
			},
			{
				name: 'wordWrapEnabled',
				type: 'boolean'
			},
			{
				name: 'saveMode',
				type: 'string'
			}
		];

		const columnsItems: IPropertyField[] = (
			properties?.get('columns') as IDataGridColumnCollection
		)
			?.filter(c => c?.dbType?.type)
			?.map(column => {
				const columnProperties = [...cloneDeep(columnPropertiesModel)];

				const getColumnValueField = (): IPropertyField => {
					switch (column.dbType.type) {
						case 'date':
						case 'dateTime': {
							return { name: 'value', type: 'date' };
						}
						case 'boolean':
							return { name: 'value', type: 'boolean' };
						case 'string': {
							if (column.editType === 'dateRangePicker') {
								return {
									name: 'value',
									type: 'object',
									items: [
										{ name: 'startDate', type: 'date' },
										{ name: 'endDate', type: 'date' }
									]
								};
							}
							return { name: 'value', type: 'string' };
						}
						case 'time':
							return { name: 'value', type: 'time' };
						case 'decimal':
							return { name: 'value', type: 'number' };
						case 'object': {
							if (column.editType == 'multiLanguageTextBox') {
								return {
									name: 'culture',
									type: 'object',
									items: captionItems
								};
							} else if (column.editType == 'comboBox') {
								const dataFields =
									properties
										.get('columns')
										.find(gridColumn => gridColumn.name === column.name)
										?.dataFields?.map(i => ({ name: i.name, type: i.type })) ||
									[];

								return {
									name: 'data',
									type: 'object',
									items: dataFields,
									readOnly: true
								};
							} else if (column.editType === 'dateRangePicker') {
								return {
									name: 'value',
									type: 'object',
									items: [
										{ name: 'startDate', type: 'date' },
										{ name: 'endDate', type: 'date' }
									]
								};
							}
						}
					}
				};

				columnProperties.push(getColumnValueField());

				return {
					name: column.name,
					type: 'object',
					items: columnProperties.filter(properties => properties)
				};
			});

		return [
			...filterFields(fields, ['readOnly']),
			{
				name: 'selectedRowKeys',
				type: 'object',
				readOnly: true,
				items: [
					{
						name: 'length',
						type: 'number'
					}
				]
			},
			{
				name: 'selectedRowsData',
				type: 'object',
				readOnly: true,
				items: [
					{
						name: 'length',
						type: 'number'
					}
				]
			},
			{
				name: 'columns',
				type: 'object',
				items: columnsItems
			},
			{
				name: 'rows',
				type: 'object',
				items: [
					...(columnsItems || []).map<IPropertyField>(({ name, type }) => ({
						name,
						type,
						hiddenOn: ['formula-manager', 'rule-manager']
					})),
					{
						name: 'length',
						type: 'number',
						hiddenOn: ['data-synchronizer'],
						readOnly: true
					}
				],
				readOnly: true,
				isArray: true
			},
			{
				name: 'displayType',
				type: 'string'
			},
			{
				name: 'wordWrapEnabled',
				type: 'boolean'
			},
			{
				name: 'columnHidingEnabled',
				type: 'boolean'
			},
			{
				name: 'cacheSettingsEnabled',
				type: 'boolean'
			},
			{
				name: 'columnFixingEnabled',
				type: 'boolean'
			},
			{
				name: 'rowDragging',
				type: 'object',
				items: [
					{
						name: 'enable',
						type: 'boolean'
					},
					{
						name: 'showDragIcons',
						type: 'boolean'
					}
				]
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			...getComponentData.call(this)
		};
	}

	dataTypeToFilterType(
		column: IControlDataGridColumn<IControlProperties>
	): BCExtremeGridEntities.ItemType {
		switch (column?.dbType?.type) {
			case 'boolean':
				return BCExtremeGridEntities.ItemType.Boolean;
			case 'date':
				return BCExtremeGridEntities.ItemType.DateTime;
			case 'dateTime':
				return BCExtremeGridEntities.ItemType.DateTimeOffset;
			case 'decimal':
			case 'int':
				return BCExtremeGridEntities.ItemType.Decimal;
			default:
				return BCExtremeGridEntities.ItemType.String;
		}
	}

	operationToFilterType(operation: FilterOperations): BCExtremeGridEntities.FilterType {
		switch (operation) {
			case 'contains':
				return BCExtremeGridEntities.FilterType.Contain;
			case 'endsWith':
				return BCExtremeGridEntities.FilterType.EndsWith;
			case 'equal':
				return BCExtremeGridEntities.FilterType.Equal;
			case 'graterThenOrEqual':
				return BCExtremeGridEntities.FilterType.GreaterOrEqual;
			case 'greaterThan':
				return BCExtremeGridEntities.FilterType.Greater;
			case 'lessThan':
				return BCExtremeGridEntities.FilterType.Less;
			case 'lessThanOrEqual':
				return BCExtremeGridEntities.FilterType.LessOrEqual;
			case 'notContains':
				return BCExtremeGridEntities.FilterType.Contain;
			case 'notEqual':
				return BCExtremeGridEntities.FilterType.Equal;
			case 'startsWith':
				return BCExtremeGridEntities.FilterType.StartsWith;
			default:
				return BCExtremeGridEntities.FilterType.Contain;
		}
	}

	__getSkipValueForPagination(pageSize: number) {
		if (
			this.__form.__fromMobile &&
			Mobile.MobileFormView.NativePlus === this.__form.__mobileFormView &&
			this.__searhcValue
		) {
			return 0;
		}

		return (this.paging.currentPage - 1) * pageSize;
	}

	getFilterColumnName(column: any) {
		if (this.saveMode === 'self') {
			return column.dbType?.columnName || column.name;
		}

		const culture = MLHelper.getSystemCulture().culture;
		return column.editorType === 'MultiLanguage' ? column.name + '.' + culture : column.name;
	}

	getFilterValue(filter: any, dataType: any) {
		let value = filter.value;
		if (filter.value && dayjs(filter.value).isValid()) {
			const date = new Date(filter.value);
			if (dataType == 'dateTime') {
				switch (filter.operation) {
					case 'equal':
					case 'lessThan':
					case 'graterThenOrEqual':
						value = new Date(date.setSeconds(0));
						break;
					case 'greaterThan':
					case 'lessThanOrEqual':
						value = new Date(date.setSeconds(59));
						break;
				}
			} else if (dataType == 'date') {
				date.setHours(0, 0, 0, 0);
				value = date;
			}
		}

		return value;
	}

	getLoadOptions(): BCExtremeGridEntities.ILoadOptions {
		let filters: any = [];
		this.__filtering
			.filter(x => !isNullOrUndefined(x.value) && x.value !== '')
			.forEach(x => {
				const column = this.columns.find(i => i.name == x.columnName);
				if (column?.dbType?.type == 'dateTime' && x.operation == 'equal') {
					const value = this.getFilterValue(x, column?.dbType?.type);
					filters.push(
						{
							Column: this.getFilterColumnName(column),
							ItemType: this.dataTypeToFilterType(column),
							Not: x.operation.includes('not'),
							Type: this.operationToFilterType('graterThenOrEqual'),
							Value: this.getFilterValue(x, column?.dbType?.type)
						},
						{
							Column: this.getFilterColumnName(column),
							ItemType: this.dataTypeToFilterType(column),
							Not: x.operation.includes('not'),
							Type: this.operationToFilterType('lessThanOrEqual'),
							Value: new Date(value.setSeconds(59))
						}
					);
				} else {
					filters.push({
						Column: this.getFilterColumnName(column),
						ItemType: this.dataTypeToFilterType(column),
						Not: x.operation.includes('not'),
						Type: this.operationToFilterType(x.operation),
						Value: this.getFilterValue(x, column?.dbType?.type)
					});
				}
			});

		this.__filtering
			.filter(x => x.values?.length)
			.forEach(x => {
				filters.push(this.__addFilter(x));
			});

		if (this.__searhcValue) {
			filters = this.__getSearchValueFilters(filters);
		}

		const pageSize = this.getPageSize();
		const paginationSkip = this.__getSkipValueForPagination(pageSize);

		return {
			filters: filters,
			pagination: this.paging.enabled
				? { skip: paginationSkip, take: pageSize }
				: ({} as any),
			sorts: this.__sorting.map(x => {
				const column = this.columns.find(i => i.name == x.columnName);
				return {
					Column: column?.dbType?.columnName || x.columnName,
					Type:
						x.direction == 'asc'
							? BCExtremeGridEntities.SortOrder.Asc
							: BCExtremeGridEntities.SortOrder.Desc
				};
			})
		};
	}

	getPageSize() {
		let pageSize = this.virtualScrolling ? 50 : this.paging.pageSize || 10;

		if (
			!this.virtualScrolling &&
			this.paging?.pagingSizes?.length &&
			this.paging.pagingSizes.findIndex(i => i === pageSize) === -1
		) {
			pageSize = this.paging.pagingSizes[0];
		}

		if (mobile().any() && this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus) {
			pageSize = this.paging.pageSize;
		}

		return pageSize;
	}
	__addFilter(x: any) {
		const column = this.columns.find(i => i.name == x.columnName);
		const filterValues = [];
		x.values.forEach(val => {
			filterValues.push({
				Column: this.getFilterColumnName(column),
				ItemType: this.dataTypeToFilterType(column),
				Not: x.filterType == 'exclude',
				Type: this.operationToFilterType('equal'),
				Value: val
			});
		});

		if (filterValues.length > 1) {
			return {
				Filters: filterValues,
				Operator: x.filterType == 'exclude' ? 1 : 2
			} as any;
		} else {
			return filterValues[0];
		}
	}
	__getSearchValueFilters(filters: any) {
		const searchFilters: any = { Filters: [], Operator: 2 };

		this.__columns.forEach(column => {
			if (isNullOrUndefined(column.searchingEnabled) || column.searchingEnabled) {
				const _column = this.columns.find(i => i.name == column.name);
				if (column.dataType == DataTypes.text || column.dataType === 'object') {
					searchFilters.Filters.push({
						Column: this.getFilterColumnName(_column),
						ItemType: BCExtremeGridEntities.ItemType.String,
						Not: false,
						Type: BCExtremeGridEntities.FilterType.Contain,
						Value: this.__searhcValue
					});
				} else if (column.dataType == 'number' && !isNaN(Number(this.__searhcValue))) {
					searchFilters.Filters.push({
						Column: this.getFilterColumnName(_column),
						ItemType: this.dataTypeToFilterType(_column),
						Not: false,
						Type: BCExtremeGridEntities.FilterType.Equal,
						Value: Number(this.__searhcValue)
					});
				}
			}
		});

		filters = [
			{
				Filters: [...filters, searchFilters],
				Operator: 1
			}
		];

		return filters;
	}

	onContextMenuOpen(args: BCExtremeGridEntities.IContextMenuArgs) {
		if (
			(args.row && args.row.isEditing !== false) ||
			(!args.row && args.target === 'content')
		) {
			return;
		}

		const isRowOrContainer =
			(this.contextMenuTarget === EContextMenuTargetTypes.Row && args.target == 'content') ||
			this.contextMenuTarget == EContextMenuTargetTypes.Container;

		const isColumnButton =
			this.contextMenuTarget == EContextMenuTargetTypes.Column &&
			args.target === 'columnButton';

		const isTargetValid = isColumnButton || isRowOrContainer;

		if (isTargetValid) {
			args.event.preventDefault();
			this.__onShowContextMenu(args.event as any, args);
			args.event.stopPropagation();
		}
	}

	colDataTypeToBatchType(type: DataTypes, precision: number): EBatchFieldTypes {
		switch (type) {
			case DataTypes.boolean:
				return EBatchFieldTypes.Boolean;
			case DataTypes.date:
				return EBatchFieldTypes.DateTime;
			case DataTypes.dateRange:
				return EBatchFieldTypes.Array;
			case DataTypes.dateTime:
				return EBatchFieldTypes.DateTimeOffset;
			case DataTypes.select:
			case DataTypes.multiLanguage:
				return EBatchFieldTypes.Object;
			case DataTypes.number:
				return precision > 0 ? EBatchFieldTypes.Decimal : EBatchFieldTypes.Integer;
			case DataTypes.text:
				return EBatchFieldTypes.String;
			default:
				return EBatchFieldTypes.Unknown;
		}
	}

	async onRowChangesChange(
		changes: Array<any>,
		changedRowId?: string,
		changedColumnName?: string,
		_newBatchOperations?: Array<IBatchOperation>
	) {
		if (this.editing?.mode != 'batch') {
			return;
		}
		const tId = this.__form.beginTransaction();
		let newBatchOperations: Array<IBatchOperation> = _newBatchOperations || [];
		if (!_newBatchOperations) {
			changes?.forEach(change => {
				newBatchOperations = [
					...newBatchOperations,
					{
						type: change.type == 'remove' ? 'delete' : change.type,
						uniqueFieldValue: change.key,
						fields: Object.keys(change.data || {}).map(key => {
							const col = this.__columns.find(x => x.name == key);
							return {
								columnName: key,
								value: change.data[key],
								type: this.colDataTypeToBatchType(
									col?.dataType as any,
									col?.editorOptions?.precision
								)
							};
						})
					}
				];
			});

			this.batchOperations.forEach(batch => {
				const batchIndex = newBatchOperations.findIndex(
					x => x.uniqueFieldValue === batch.uniqueFieldValue
				);
				if (batchIndex > -1) {
					newBatchOperations[batchIndex].fields.push(
						...batch.fields.filter(
							f =>
								!newBatchOperations[batchIndex].fields.find(
									i => i.columnName == f.columnName
								)
						)
					);
				} else {
					newBatchOperations.push({ ...batch });
				}
			});
		}

		const payload: IBatchChangesEventArgs = {
			batchOperations: newBatchOperations,
			cancel: false,
			changedColumnName: changedColumnName,
			changedRowId: changedRowId
		};
		await this.emit('event:OnBatchChanges', payload);
		if (!payload.cancel) {
			this.__proxyChanging.batchOperations = newBatchOperations;
		}

		this.__form.endTransaction(tId);
		this.__proxyChanging.loading = false;
	}

	onRowDoubleClick(row: any) {
		this.emit('event:OnRowDoubleClick', { row });
	}

	async setCellValueEvent(this: DataGrid, args: ISetCellValueChangeEventArgs) {
		args.newData[args.columnName] = args.value;

		this.columns
			.filter(c => c.editType == 'comboBox')
			?.forEach(column => {
				const control: any = column.editControl;
				if (control.dataSource?.name && control.dataSource?.parameters?.length) {
					control.dataSource.parameters.forEach(param => {
						if (param?.valueModifier == 'fieldSelection') {
							const fieldArr = param.field.split('.');
							const propertyPath = fieldArr.join('.');
							if (propertyPath.includes(`columns.${args.columnName}.`)) {
								args.newData[column.name] = null;
								args.newData[`__${column.name}`] = null;
							}
						}
					});
				}
			});

		await this.emit('event:SetCellValue', args);
		return Promise.resolve(args);
	}

	async onRowRemoving(valueArgs: any): Promise<boolean | void> {
		await this.emit('event:OnRowRemoving', valueArgs);

		if (!valueArgs.cancel) {
			const args = await this.emit<IUserEvent<IRowRemovingEventArgs>>(
				'event:server:OnRowRemoving',
				{
					argument: valueArgs,
					controlId: this.name,
					name: 'OnRowRemoving',
					sender: 'web',
					guid: createGuid()
				}
			);

			const executedServerEvent: IControlExecutedServerEvent = args[0]?.executedServerEvent;

			if (!executedServerEvent || !executedServerEvent?.parameters.cancel) {
				return Promise.resolve();
			} else {
				return true;
			}
		} else {
			return true;
		}
	}

	async onRowRemoved({ row }: { row: IDictionary<any>; rows: Array<IDictionary<any>> }) {
		row = getBaseRowData.call(this, row);
		this.emit('event:OnRowRemoved', { row });
		await this.emit<IUserEvent<IRowRemovedEventArgs>>('event:server:OnRowRemoved', {
			argument: { row },
			controlId: this.name,
			name: 'OnRowRemoved',
			sender: 'web',
			guid: createGuid()
		});
		this.reload(true);
	}

	onCellPrepared(args: any) {
		if (args.rowType === 'data' && args.data[args.column.dataField]) {
			const cellPreparedArgs: ICellPreparedEventArgs = {
				data: args.data,
				column: args.column,
				cellElement: args.cellElement
			};
			this.emit('event:OnCellPrepared', cellPreparedArgs);
		}
	}

	async onInitNewRow(): Promise<any> {
		this.pendingEdit = true;

		if (this.saveMode == 'self' && this.__documentId == 0) {
			if (this.__form.flowSecretKey) {
				BCNotification.warn({ message: findMessage.get('103029') });
				this.pendingEdit = false;
				return Promise.reject();
			} else {
				this.__form.saveForm(true);
			}
		}

		const defaultValues = this.columns.filter(item => !isNullOrUndefined(item.defaultValue));
		const data = Object.assign(
			{},
			...defaultValues.map(item => ({ [item.name]: item.defaultValue }))
		);

		const valueArgs = { data };
		await this.emit('event:OnInitNewRow', valueArgs);
		return Promise.resolve(valueArgs.data);
	}

	onReorder(e: any) {
		const visibleRows = e.component.getVisibleRows();
		let newRows = [...this.rows];
		const toIndex = newRows.findIndex(
			row =>
				row.cells.find(cell => cell.name === this.getUniqueDataField())?.value ===
				visibleRows[e.toIndex].data[this.getUniqueDataField()]
		);
		const fromIndex = newRows.findIndex(
			row =>
				row.cells.find(cell => cell.name === this.getUniqueDataField())?.value ===
				e.itemData[this.getUniqueDataField()]
		);
		const rowToMove = newRows.find(
			row =>
				row.cells.find(cell => cell.name === this.getUniqueDataField())?.value ===
				e.itemData[this.getUniqueDataField()]
		);

		newRows.splice(fromIndex, 1);
		newRows.splice(toIndex, 0, rowToMove);
		newRows = newRows.map((row, index) => {
			(row.data as any).index = index;
			row.index = index;
			return row;
		});

		this.__proxyChanging.rows = newRows;
	}

	async onRowUpdating(valueArgs: IRowUpdatingEventArgs): Promise<boolean | void | any> {
		let updateRow = null;
		this.rows.forEach(row => {
			const cell = row.cells.find(r => r.name == this.getUniqueDataField());
			let value = cell?.value;
			if (cell?.editType === 'datePicker') {
				value = dayjs(value).format('YYYY-MM-DD');
			}
			if (value == valueArgs.oldRow[this.getUniqueDataField()]) {
				updateRow = getNewRow.call(
					this,
					this.checkColumnFormulas(valueArgs.newRow),
					row,
					true
				);
			}
		});
		const duplicateControl = await this.checkDuplicateColumn(updateRow, true);
		if (!duplicateControl) {
			return true;
		}
		await this.emit('event:OnRowUpdating', valueArgs);

		if (!valueArgs.cancel) {
			const args = await this.emit<IUserEvent<any>>('event:server:OnRowUpdating', {
				argument: valueArgs,
				controlId: this.name,
				name: 'OnRowUpdating',
				sender: 'web',
				guid: createGuid()
			});

			const executedServerEvent: IControlExecutedServerEvent = args[0]?.executedServerEvent;

			if (!executedServerEvent || !executedServerEvent?.parameters.cancel) {
				if (executedServerEvent?.parameters?.newRow) {
					valueArgs.newRow = executedServerEvent.parameters.newRow;
				}
				return Promise.resolve(valueArgs);
			} else {
				return true;
			}
		} else {
			return true;
		}
	}

	onSummaryChanged(args: any) {
		this.emit('event:OnSummaryChanged', {
			type: args.type,
			columnName: args.columnName,
			value: args.value
		});
		if (!this.__summaryValue[args.columnName]) {
			this.__summaryValue[args.columnName] = {};
		}
		this.__summaryValue[args.columnName][args.type] = {
			controlName: this.name,
			columnName: args.columnName,
			type: args.type,
			value: args.value
		};
		Mobile.MobileView.setControlProperties(
			this.name,
			this.__getComponentData(),
			this.__form?.__windowId || ''
		);
	}

	onEditCanceling(args: any) {
		this.pendingEdit = false;
	}

	async onEditingStart(args: any) {
		this.pendingEdit = true;
		const payload = {
			columnName: args.column?.name,
			row: args.data,
			rowId: args.key
		} as IEditingStartEventArgs;
		await this.emit('event:OnEditingStart', payload);

		if (this.isMobile() && this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus) {
			Object.keys(payload.row || {}).forEach(field => {
				const foundColumn = this.__columns.find(col => col.name === field);

				if (foundColumn?.editorType === 'SelectBox') {
					this.__editingColumnsValues.forEach(col => {
						if (
							col.name === field &&
							payload.row[field] &&
							typeof payload.row[field] === 'object'
						) {
							col.rowValue = payload.row[field].value;
						}
					});
				}
			});

			const mobileControls = convertFieldsForMobile(
				this.columns as any,
				this.__editingColumnsValues,
				this.__columns,
				this.name,
				true,
				payload
			);
			Mobile.MobileView.setDataGridControlsProperties(mobileControls, this.name);
			this.__dataGridRef.instance?.()?.cancelEditData();
		}
	}

	async onRowUpdated(args: {
		newRow: IDictionary<any>;
		oldRow: IDictionary<any>;
		rows: Array<IDictionary<any>>;
	}) {
		const newRow = _merge(args.oldRow, args.newRow);
		const oldRow =
			this.rows.find(
				i => i.data[this.getUniqueDataField()] == newRow[this.getUniqueDataField()]
			)?.data || args.oldRow;
		args.newRow = getBaseRowData.call(this, newRow);
		args.oldRow = getBaseRowData.call(this, oldRow);
		this.emit('event:OnRowUpdated', { newRow: newRow, oldRow: oldRow });
		await this.emit<IUserEvent<IRowUpdatedEventArgs>>('event:server:OnRowUpdated', {
			argument: { newRow: newRow, oldRow: oldRow },
			controlId: this.name,
			name: 'OnRowUpdated',
			sender: 'web',
			guid: createGuid()
		});
		this.reload(true);
	}

	checkDuplicateColumn(row: any, isUpdating?: boolean): Promise<boolean> {
		return new Promise(async (resolve, reject) => {
			let control = true;
			if (this.columns.filter(col => col.checkUniqueValue).length) {
				if (this.__form.documentId && this.saveMode == 'self') {
					const url = await this.getSelfUrl('CheckColumnsIsUnique');
					try {
						const res = await getOperationData(url, {
							documentId: this.__documentId,
							controlName: this.name,
							rows: [row]
						});
						control = res.data.isValid;
						res.data.invalids.map(invalid => {
							invalid.invalidColumns.map(invalidColumn => {
								BCNotification.warning({
									message: findMessage.get('103133').replace('{0}', invalidColumn)
								});
							});
						});
					} catch (err) {
						MessageBox.error({ error: err.exception.data });
						return reject(err);
					}
				} else if (this.dataSource.name === '' && this.dataSourceType === 'dynamic') {
					for (const col of this.columns.filter(col => col.checkUniqueValue)) {
						if (this.checkDuplicateColumnControl(row, col, isUpdating)) {
							control = false;
							BCNotification.warning({
								message: findMessage.get('103133').replace('{0}', col.name)
							});
						}
					}
				}
			}
			resolve(control);
		});
	}

	checkDuplicateColumnControl(row: any, column: any, isUpdating?: boolean): boolean {
		const data = isUpdating
			? this.rows.filter(
					r => r.data[this.getUniqueDataField()] !== row.data[this.getUniqueDataField()]
			  )
			: this.rows;
		const duplicateRows = data.filter(r => {
			if (r.data[column.name] && row.data[column.name]) {
				if (column.dbType.type == 'date') {
					return (
						dayjs(r.data[column.name]).format(column.format ?? 'L') ==
						dayjs(row.data[column.name]).format(column.format ?? 'L')
					);
				} else if (column.dbType.type == 'dateTime') {
					return (
						dayjs(r.data[column.name]).format(column.format ?? 'L LT') ==
						dayjs(row.data[column.name]).format(column.format ?? 'L LT')
					);
				} else if (column.dbType.type == 'time') {
					return (
						(typeof r.data[column.name] === 'string' &&
						!dayjs(r.data[column.name]).isValid()
							? r.data[column.name]
							: dayjs(r.data[column.name]).format(column.format ?? 'HH:mm')) ==
						(typeof row.data[column.name] === 'string' &&
						!dayjs(row.data[column.name]).isValid()
							? row.data[column.name]
							: dayjs(row.data[column.name]).format(column.format ?? 'HH:mm'))
					);
				} else if (column.editType == 'multiLanguageTextBox') {
					const userCulture = this.__form.currentLanguage;
					const defaultCulture = this.__form.supportedLanguages.find(l => l.isDefault);

					const rowValue =
						typeof row.data[column.name] == 'string'
							? row.data[column.name]
							: row.data[column.name][userCulture?.culture] ||
							  row.data[column.name][defaultCulture?.culture];
					const rValue =
						typeof r.data[column.name] == 'string'
							? r.data[column.name]
							: r.data[column.name][userCulture?.culture] ||
							  r.data[column.name][defaultCulture?.culture];

					return rowValue == rValue;
				} else if (column.editType == 'comboBox') {
					if (column.dbType.type == 'string' || column.dbType.type == 'decimal') {
						return r.data[column.name] == row.data[column.name];
					} else {
						return (r.data[column.name] as any)?.value == row.data[column.name]?.value;
					}
				} else {
					return r.data[column.name] == row.data[column.name];
				}
			} else {
				return false;
			}
		});

		return duplicateRows.length > 0;
	}

	async onRowInserting(valueArgs: any): Promise<boolean | void> {
		const duplicateControl = await this.checkDuplicateColumn(
			getNewRow.call(this, this.checkColumnFormulas(valueArgs.row))
		);
		if (!duplicateControl) {
			return true;
		}

		this.checkColumnFormulas(valueArgs.row);

		await this.emit('event:OnRowInserting', valueArgs);

		if (!valueArgs.cancel) {
			const args = await this.emit<IUserEvent<IRowInsertingEventArgs>>(
				'event:server:OnRowInserting',
				{
					argument: valueArgs,
					controlId: this.name,
					name: 'OnRowInserting',
					sender: 'web',
					guid: createGuid()
				}
			);

			const executedServerEvent: IControlExecutedServerEvent = args[0]?.executedServerEvent;

			if (!executedServerEvent || !executedServerEvent?.parameters.cancel) {
				return Promise.resolve();
			} else {
				return true;
			}
		} else {
			return true;
		}
	}

	async onRowInserted({ row }: { row: IDictionary<any>; rows: Array<IDictionary<any>> }) {
		row = getBaseRowData.call(this, row);
		this.emit('event:OnRowInserted', { row });
		await this.emit<IUserEvent<IRowInsertedEventArgs>>('event:server:OnRowInserted', {
			argument: { row },
			controlId: this.name,
			name: 'OnRowInserted',
			sender: 'web',
			guid: createGuid()
		});
		this.reload(true);
	}

	onSelectionChange(selectionData: Array<any>, selectedIds: Array<string>) {
		return new Promise<void>(resolve => {
			const tId = this.__form.beginTransaction();
			this.__proxyChanging.selectedRowKeys = selectedIds;
			this.__proxyChanging.selectedRowsData = selectionData;
			this.emit('event:OnSelectionChanged', { items: selectionData });
			this.__form.endTransaction(tId).then(() => {
				resolve();
			});
		});
	}

	async onSelectionChanging(args: ISelectionChangingEventArgs) {
		await this.emit('event:OnSelectionChanging', args);
	}

	async onInsertSelf(rows: IDataGridRowCollection) {
		if (this.__hasPendingInsertRows) {
			return;
		}

		this.__hasPendingInsertRows = true;
		const url = await this.getSelfUrl('InsertRows');

		try {
			await getOperationData(url, {
				documentId: this.__documentId,
				controlName: this.name,
				columns: this.columns,
				rows
			});

			if (this.saveMode == 'self' && this.__documentId) {
				await this.onLoadSelf();
			}
		} catch (err) {
			MessageBox.error({ error: err.exception.data });

			if (this.saveMode == 'self' && this.__documentId) {
				await this.onLoadSelf();
			}
		} finally {
			this.__hasPendingInsertRows = false;
			this.__formSaveControl = false;
		}
	}

	async onCommitChangesSelf(changes: BCExtremeGridEntities.IChangeSet, newRow?: any) {
		const rows: IDataGridRowCollection = this.rows;

		switch (changes?.type) {
			case 'insert':
				if (newRow) {
					const url = await this.getSelfUrl('InsertRows');
					const row = getNewRow.call(this, this.checkColumnFormulas(newRow));
					try {
						await getOperationData(url, {
							documentId: this.__documentId,
							controlName: this.name,
							columns: this.columns,
							rows: [row]
						});
					} catch (err) {
						MessageBox.error({ error: err.exception.data });
					} finally {
						this.reload(true);
					}
				}
				break;
			case 'remove':
				const rowId = rows.find(row => row.data[this.getUniqueDataField()] == changes.key)
					?.rowId;
				const url = await this.getSelfUrl('DeleteRows');
				if (rowId) {
					try {
						await getOperationData(url, {
							documentId: this.__documentId,
							controlName: this.name,
							rowIds: [rowId]
						});
					} catch (err) {
						MessageBox.error({ error: err.exception.data });
					} finally {
						this.reload(true);
					}
				} else {
					this.__proxyChanging.rows = rows.filter(row => {
						const primaryKeyValue = row.cells.find(
							r => r.name == this.getUniqueDataField()
						)?.value;
						const column = this.columns.find(
							col => col.name === this.getUniqueDataField()
						);
						return compareDeletingRowByCellType(
							primaryKeyValue,
							changes.key,
							column?.dbType?.type,
							column.format
						);
					});
				}
				break;
			case 'update':
				if (newRow) {
					let updateRow = null;
					rows.forEach(row => {
						const cell = row.cells.find(r => r.name == this.getUniqueDataField());
						let value = cell?.value;
						if (cell.editType === 'datePicker') {
							value = dayjs(value).format('YYYY-MM-DD');
						}
						if (value == changes.key) {
							updateRow = getNewRow.call(this, this.checkColumnFormulas(newRow), row);
						}
					});

					const url = await this.getSelfUrl('UpdateRow');
					try {
						await getOperationData(url, {
							documentId: this.__documentId,
							controlName: this.name,
							columns: this.columns,
							row: updateRow
						});
					} catch (err) {
						MessageBox.error({ error: err.exception.data });
					} finally {
						this.reload(true);
					}
				}
				break;
			default:
				break;
		}

		this.__editingColumnsValues = this.columns.map(c => ({
			name: c.name,
			value: null,
			rowValue: null
		}));

		if (changes?.type != 'remove') {
			Mobile.MobileView.gridRowCommitResponse({ status: true });
		}
	}

	onCommitChanges(changes: BCExtremeGridEntities.IChangeSet, newRow?: any) {
		this.pendingEdit = false;
		if (this.saveMode == 'self' && this.__documentId != 0) {
			this.onCommitChangesSelf(changes, newRow);
		} else {
			let rows: IDataGridRowCollection = this.rows;

			switch (changes?.type) {
				case 'insert':
					if (newRow) {
						rows.push(getNewRow.call(this, this.checkColumnFormulas(newRow)));
					}
					break;
				case 'remove':
					rows = rows.filter(row => {
						const primaryKeyValue = row.cells.find(
							r => r.name == this.getUniqueDataField()
						)?.value;
						const column = this.columns.find(
							col => col.name === this.getUniqueDataField()
						);
						return compareDeletingRowByCellType(
							primaryKeyValue,
							changes.key,
							column?.dbType?.type,
							column.format
						);
					});
					break;
				case 'update':
					if (newRow) {
						rows = rows.map(row =>
							row.cells.find(r => r.name == this.getUniqueDataField())?.value ==
							changes.key
								? getNewRow.call(this, this.checkColumnFormulas(newRow), row, true)
								: row
						);
					}
					break;
				default:
					break;
			}

			if (
				this.editing.mode === 'cell' &&
				changes?.type != 'remove' &&
				this.__isUpdateColumnCells
			) {
				this.__commitIdFixed = true;
			}

			const tId = this.__form.beginTransaction();
			this.__proxyChanging.rows = rows;
			this.__form.endTransaction(tId).then(() => {
				this.__commitIdFixed = false;
				this.__isUpdateColumnCells = false;
			});

			this.__editingColumnsValues = this.columns.map(c => ({
				name: c.name,
				value: null,
				rowValue: null
			}));

			if (changes?.type != 'remove') {
				Mobile.MobileView.gridRowCommitResponse({ status: true });
			}
		}
	}

	onColumnResizeChange(data: any[]) {
		this.__columns = this.__columns.map(column => ({
			...column,
			width: data.find(item => item.columnName == column.name)?.width || column.width
		}));

		this.__proxyChanging.columns = this.columns.map(column => ({
			...column,
			width: data.find(item => item.columnName == column.name)?.width || column.width
		}));
	}

	onGridUpdated(
		props: BCExtremeGridEntities.IExtremeGridProps,
		prevProps: BCExtremeGridEntities.IExtremeGridProps
	) {
		if (this.dataSourceType == 'relation') {
			triggerRowEvents.call(this, props, prevProps);
		}

		if (this.selectionForm) {
			this.__getSelectionFormUpdate(prevProps.dataSource);
		}
	}

	async onPageIndexChange(pageIndex) {
		if (this.virtualScrolling) {
			return;
		}

		const tId = this.__form.beginTransaction();
		this.__proxyChanging.paging = {
			...this.paging,
			currentPage: pageIndex + 1
		};
		await this.__form.endTransaction(tId);
		this.reload(false);
	}

	async onPageSizeChange(pageSize, pageIndex) {
		if (this.virtualScrolling) {
			return;
		}
		const tId = this.__form.beginTransaction();
		this.__proxyChanging.paging = {
			...this.paging,
			pageSize: pageSize,
			currentPage: !isNullOrUndefined(pageIndex) ? pageIndex + 1 : this.paging.currentPage
		};
		await this.__form.endTransaction(tId);
		this.reload(false);
	}

	async onSortingChange(sorting: Array<BCExtremeGridEntities.ISortingColumn>) {
		const tId = this.__form.beginTransaction();
		this.__sorting = sorting;
		this.__columns.forEach(column => {
			const columnSorting = sorting.find(c => column.name == c.columnName);
			if (columnSorting) {
				column.sorting = { order: columnSorting.direction, index: columnSorting.index };
			} else {
				column.sorting = null;
			}
		});

		await this.__form.endTransaction(tId);
		this.reload(false);
	}

	async onColumnFixingChange(columns: Array<BCExtremeGridEntities.IFixingColumn>) {
		const tId = this.__form.beginTransaction();
		this.__columns.forEach(column => {
			const fixedColumn = columns.find(c => column.name == c.columnName);
			if (fixedColumn) {
				column.fixed = fixedColumn.fixed;
				column.fixedPosition = fixedColumn.fixedPosition;
			}
		});

		await this.__form.endTransaction(tId);
	}

	onSearchingChange(value: string) {
		this.__searhcValue = value;

		if (this.paging.currentPage != 1) {
			this.__dataGridRef?.instance?.()?.option('paging', {
				enabled: this.paging.enabled,
				pageSize: this.paging.pageSize,
				pageIndex: 0
			});
			this.onPageIndexChange(0);
		} else {
			this.reload(false);
		}
	}

	async onClearAll() {
		const tId = this.__form.beginTransaction();
		// set visible true columns
		this.__proxyChanging.columns = this.columns?.map(column => ({ ...column, visible: true }));

		// reset filtering
		this.__filtering = [];
		this.__dataGridRef?.instance?.()?.clearFilter?.();

		if (!this.virtualScrolling && this.paging.currentPage != 1) {
			this.__dataGridRef?.instance?.()?.option?.('paging', {
				enabled: this.paging.enabled,
				pageSize: this.paging.pageSize,
				pageIndex: 0
			});
			this.__proxyChanging.paging = { ...this.paging, currentPage: 1 };
		}

		// reset sorting
		this.__sorting = [];
		this.__columns.forEach(column => (column.sorting = null));

		await this.__form.endTransaction(tId);
		this.reload(false);
	}

	async onColumnVisibilityChange(hiddenColumns: Array<string>) {
		const tId = this.__form.beginTransaction();
		this.__proxyChanging.columns = this.columns?.map(column => {
			if (hiddenColumns.includes(column.name)) {
				return { ...column, visible: false };
			}
			return { ...column, visible: true };
		});
		await this.__form.endTransaction(tId);
	}

	async onFilteringChange(filterItems: Array<BCExtremeGridEntities.IFilterItem>) {
		const tId = this.__form.beginTransaction();
		this.__filtering = filterItems;

		if (this.saveMode == 'self' || (this.dataSource?.name && !this.dataSource.runAtServer)) {
			if (this.paging.currentPage != 1) {
				this.__dataGridRef?.instance?.()?.option('paging', {
					enabled: this.paging.enabled,
					pageSize: this.paging.pageSize,
					pageIndex: 0
				});
				this.onPageIndexChange(0);
			} else {
				this.reload(false);
			}
		}

		await this.__form.endTransaction(tId);
	}

	async onFilterClear() {
		this.__columns = this.__columns.map(column => {
			let filtering = null;
			if (
				column?.editorType == 'Button' ||
				column?.editorType == 'None' ||
				column?.formatType == 'image'
			) {
				filtering = {
					enabled: false
				};
			}

			column.filtering = filtering;
			return column;
		});

		this.__filtering = [];
		if (this.paging.currentPage != 1) {
			this.__dataGridRef?.instance?.()?.option('paging', {
				enabled: this.paging.enabled,
				pageSize: this.paging.pageSize,
				pageIndex: 0
			});
			this.onPageIndexChange(0);
		} else {
			this.reload(false);
		}
	}

	onForceRefresh() {
		this.reload(true);
	}

	onEditorCreating(args: any) {
		if (args.column.dataType === 'object' && !args.column.editorType) {
			return <span></span>;
		}
	}

	onEditorPreparing(args: any) {
		if (
			args.column?.dataType === 'object' &&
			args.editorOptions &&
			args.column.editorType == 'SelectBox'
		) {
			args.editorOptions.value =
				args.column?.tag?.getValueType == 'keyTextValue'
					? args.value?.value || null
					: args.value;
			return args;
		}
	}

	noDataTemplateAndSetMaxSize(content: any) {
		const contentElement: HTMLElement = content.element;
		const rowsViewElement = contentElement.getElementsByClassName(
			'dx-datagrid-rowsview'
		)[0] as HTMLElement;

		if (rowsViewElement) {
			if (this.displayType == 'MaxHeight') {
				rowsViewElement.style.maxHeight = this.height + 'px';
			} else if (this.displayType == 'FixedHeight') {
				rowsViewElement.style.height = this.height + 'px';
				rowsViewElement.classList.remove('dx-empty');
			}
		}
	}

	async getHeaderFilterData(options: any, column: IExtremeGridColumn) {
		try {
			const data = [];
			const loadOptions = {
				selectors: [column.name],
				distinct: true,
				pagination: { skip: options.skip, take: options.take }
			};

			const externalData = { fieldParams: this.getFieldParams() };
			const result = await this.__onLoadExportData({
				externalData,
				loadOptions,
				reload: true
			});

			if (result.rows.length > 0) {
				data.push(
					...result.rows.map(row => {
						const value = row.data[column.name];
						return { text: this.getHeaderText(column, value), value: value };
					})
				);
			}
			return data;
		} catch (error) {
			console.log(error);
		}
	}

	async __onLoadData(loadOptions: any) {
		const result = await this.__onLoad({ loadOptions });
		return this.getDatasourceUpdate(result.rows);
	}

	async __onLoadExportData({ externalData, reload, forceRefresh, loadOptions }: IOnLoadArgs) {
		if (
			this.__form.displayMode === 'preview' ||
			(!this.__isInitialized && this.dataSource?.parameters?.length && !reload)
		) {
			return;
		}

		if (this.dataSource?.name && !this.dataSource.runAtServer) {
			try {
				const response = await getDataSourceData({
					dataSource: this.dataSource,
					projectName: this.__form.projectName,
					clientCacheId: this.__form.__windowId,
					fieldParams: externalData?.fieldParams,
					reload,
					forceRefresh,
					loadOptions: loadOptions,
					projectVersion: this.__form.packageVersion
				});

				if (Array.isArray(response?.data?.result)) {
					const rows = response.data.result.map(this.getRowData);
					return { rows: rows, totalCount: response.data.count };
				}
			} catch (e) {
				console.log(e);
				return { rows: [], totalCount: 0 };
			}
		}
	}

	async __onLoadExportDataSelfMode(loadOptions: any) {
		try {
			const url = await this.getSelfUrl('getRows');
			const response = await getOperationData(url, {
				documentId: this.__documentId,
				controlName: this.name,
				columns: this.columns,
				loadOptions: loadOptions
			});
			// .then(async response => {
			//     let rows = response.data.rows.map(this.getRowData);
			//     return { rows: rows, totalCount: response.data.count }
			// }).catch(err => {
			//     console.log(err);
			//     return { rows: [], totalCount: 0 }
			// })
			if (Array.isArray(response?.data?.rows)) {
				return { rows: response.data.rows, totalCount: response.data.count };
			}
		} catch (e) {
			console.log(e);
			return { rows: [], totalCount: 0 };
		}
	}

	getHeaderText(column: IExtremeGridColumn, value: any) {
		switch (column.dataType) {
			case 'boolean':
				return value ? findMessage.get('100587') : findMessage.get('100588');
			default:
				return value;
		}
	}

	getFieldParams(): IDictionary<any> {
		return this.dataSource.parameters?.reduce((result, param) => {
			if (param.valueModifier === 'valueEntry') {
				result[param.name] = param.value;
			} else if (param.valueModifier === 'fieldSelection') {
				const field = param.field.split('.');
				const controlId = field[0];
				const propPath = field.slice(1, field.length).join('.');
				result[param.field] = getValueControl(
					this.__form,
					controlId,
					getActionValue(controlId, propPath, this.__form.items as any)
				);
			}
			return result;
		}, {});
	}

	async __onExportData() {
		try {
			const data = [];
			let skip = 0;
			const loadOptions = this.getLoadOptions();
			this.__proxyChanging.loading = true;
			const externalData = { fieldParams: this.getFieldParams() };
			while (true) {
				loadOptions.pagination = { skip: skip, take: 200 };

				let result;

				if (this.saveMode == 'self') {
					result = await this.__onLoadExportDataSelfMode(loadOptions);
				} else {
					result = await this.__onLoadExportData({
						externalData,
						loadOptions,
						reload: true
					});
				}

				if (result.totalCount > 0) {
					data.push(...this.getDatasourceUpdate(result.rows)?.data);
				} else {
					break;
				}

				if (this.__totalCount > skip) {
					skip += 200;
				} else {
					break;
				}
			}
			return data;
		} catch (error) {
			console.log(error);
		} finally {
			this.__proxyChanging.loading = false;
		}
	}

	getGridRef(ref: any, gridMethods?: any) {
		this.__dataGridRef = ref;
		this.__methods = gridMethods.methods;
	}

	__moreRowsControl: boolean = false;
	async getMoreRows(refresh?: boolean) {
		if (
			this.virtualScrolling &&
			this.rows.length < this.__totalCount &&
			!this.__moreRowsControl
		) {
			this.__moreRowsControl = true;
			const take =
				this.__form.__fromMobile &&
				this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
					? 20
					: 50;
			try {
				const rows = refresh ? [] : this.rows;
				const skip = refresh ? 0 : this.rows.length;
				const loadOptions = this.getLoadOptions();
				this.__proxyChanging.loading = true;
				const externalData = { fieldParams: this.getFieldParams() };
				loadOptions.pagination = { skip: skip, take: take };

				const result = await this.__onLoadExportData({
					externalData,
					loadOptions,
					reload: true
				});

				if (result.rows.length > 0) {
					result.rows.forEach(item => {
						rows.push(item);
					});
					this.__proxyChanging.rows = rows;
				}
			} catch (error) {
				console.log(error);
			} finally {
				this.__moreRowsControl = false;
				this.__proxyChanging.loading = false;
			}
		}
	}

	sortColumn({ columnName, sortOrder, sortIndex }: IDataGridColumnSortArgs) {
		this.__form.applyChanges().then(() => {
			const options: Omit<IDataGridColumnSortArgs, 'columnName'> = { sortOrder };

			if (!isNullOrUndefined(sortIndex)) {
				options.sortIndex = sortIndex;
			}

			this.__dataGridRef?.instance?.()?.columnOption(columnName, options);
		});
	}

	__checkIncreaseCommitId(path: string[]) {
		return this.__commitIdFixed;
	}

	__render(children?: JSX.Element) {
		if (children) {
			return super.__render(children);
		}
		return super.__render(
			<div
				id="designer-height"
				style={{ ...this.style }}
			>
				<BCLoading show={this.loading}>
					<BCExtremeGrid.Core
						key={this.__gridId}
						useStateForSelection
						willReceivePropsTriggerEnabled
						onContextMenuPreparing={this.onContextMenuOpen}
						onRowChangesChange={this.onRowChangesChange}
						onLoadData={this.__onLoadData}
						onExportData={this.__onExportData}
						getInputReference={this.getGridRef}
						onLoadMoreRows={this.getMoreRows}
						{...getDataGridProps.call(this)}
						isMobile={this.isMobile()}
					/>
				</BCLoading>
			</div>
		);
	}
}
